import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { customAxiosInstanceMutator } from './custom-axios-instance';

/**
 * Charges the business controls (presentation, 23 Oct 2026): stop automatic charges on a loan and
 * reverse an approved charge (#37), and a customer's personal charge terms (#38).
 */
export interface ChargeControls {
  accountId: string;
  chargesStopped: boolean;
  chargesStoppedReason?: string | null;
  chargesStoppedBy?: string | null;
  chargesStoppedAt?: string | null;
  /** The loan's default terms were set on the loan itself, so customer terms do not replace them */
  personalTerms: boolean;
}

export interface CustomerChargeTerms {
  customerPartyId: string;
  defaultInterestRate?: number | null;
  defaultChargeFrequency?: string | null;
  defaultChargeIntervalDays?: number | null;
  defaultGraceDays?: number | null;
  reason?: string | null;
  modifiedBy?: string | null;
  modifiedDate?: string | null;
  /** On save: how many open loans took the terms, and how many kept their own */
  loansUpdated?: number | null;
  loansWithOwnTerms?: number | null;
}

const controlsKey = (accountId: string) => ['/loan/api/v1/ledger/accounts', accountId, 'charge-controls'];
const termsKey = (customerPartyId: string) => ['/loan/api/v1/customers', customerPartyId, 'charge-terms'];

export const useChargeControls = (accountId?: string) => useQuery({
  queryKey: controlsKey(accountId || ''),
  queryFn: ({ signal }) => customAxiosInstanceMutator<ChargeControls>({
    url: `/loan/api/v1/ledger/accounts/${accountId}/charge-controls`, method: 'GET', signal }),
  enabled: !!accountId,
});

export const useSetChargesStopped = (accountId: string) => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: { stopped: boolean; reason?: string }) => customAxiosInstanceMutator<ChargeControls>({
      url: `/loan/api/v1/ledger/accounts/${accountId}/charges-stopped`, method: 'PUT', data: body,
      headers: { 'Content-Type': 'application/json' } }),
    onSuccess: saved => client.setQueryData(controlsKey(accountId), saved),
  });
};

export const reverseCharge = (reviewId: string, reason: string) => customAxiosInstanceMutator<unknown>({
  url: `/loan/api/v1/ledger/penalties/reviews/${reviewId}/reverse`, method: 'POST', data: { reason },
  headers: { 'Content-Type': 'application/json' } });

/** The customer's personal charge terms; undefined when they have none (the API answers 204). */
export const useCustomerChargeTerms = (customerPartyId?: string) => useQuery({
  queryKey: termsKey(customerPartyId || ''),
  queryFn: async ({ signal }) => (await customAxiosInstanceMutator<CustomerChargeTerms | ''>({
    url: `/loan/api/v1/customers/${customerPartyId}/charge-terms`, method: 'GET', signal })) || null,
  enabled: !!customerPartyId,
  retry: false,
});

export const useSaveCustomerChargeTerms = (customerPartyId: string) => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: Omit<CustomerChargeTerms, 'customerPartyId'>) => customAxiosInstanceMutator<CustomerChargeTerms>({
      url: `/loan/api/v1/customers/${customerPartyId}/charge-terms`, method: 'PUT', data: body,
      headers: { 'Content-Type': 'application/json' } }),
    onSuccess: saved => client.setQueryData(termsKey(customerPartyId), saved),
  });
};
